"use client";

import Link from "next/link";
import Script from "next/script";
import React, { useState } from "react";
import { FaChevronDown, FaPhone } from "react-icons/fa";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showTerms, setShowTerms] = useState(false);

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (typeof grecaptcha === "undefined") {
      console.error("reCAPTCHA client is not available.");
      setErrorMessage("reCAPTCHA client is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, {
          action: "submit",
        });

        const response = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, token, formType: "contact" }),
        });

        if (response.ok) {
          const responseData = await response.json();
          setSuccessMessage(responseData.message);
          setSubmitted(true);
          setErrorMessage("");

          // GTM event tracking
          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "trichos_form_submit",
              formType: "appointment",
              formId: "trichos-appointment-form",
              formName: "Appointment Form",
              submissionStatus: "success",
            });
          }

          // Redirect to thank you page after a short delay
          setTimeout(() => {
            window.location.href = "/thank-you-consultation";
          }, 1000);
        } else {
          const errorData = await response.json();
          setErrorMessage(errorData.message || "Error submitting form.");
          setSuccessMessage("");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrorMessage("An error occurred. Please try again.");
        setSuccessMessage("");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      city: "",
    });
    setSubmitted(false);
    setSuccessMessage("");
    setErrorMessage("");
  };

  return (
    <section className="w-full">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />

      <div className="">
        <div className="">
          {submitted ? (
            <div className="text-center">
              {successMessage && (
                <p className="text-xl text-green-500">{successMessage}</p>
              )}
              {errorMessage && (
                <p className="text-xl text-red-500">{errorMessage}</p>
              )}
              <button
                onClick={handleReset}
                className="mt-4 rounded bg-purple-950 px-4 py-2 font-bold text-white hover:bg-rose-400"
              >
                Reset Form
              </button>
            </div>
          ) : (
            // Ensure your form has the ID "trichos-appointment-form"
            <form onSubmit={handleSubmit} id="trichos-appointment-form">
              <div className="w-full rounded-xl bg-white shadow-sm">
                {/* Header */}
                <div className="flex items-center gap-2 rounded-t-xl bg-violet-100 px-3 py-1.5">
                  <FaPhone className="size-4 shrink-0 text-violet-700" />
                  <p className="text-xs font-bold text-violet-700">
                    Book a consultation
                  </p>
                </div>

                {/* Form Body */}
                <div className="px-4 pb-4 pt-2">
                  <div className="mb-2 flex flex-col gap-3">
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Name"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email (optional)"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="Mobile Number"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        placeholder="City"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                    </div>
                  </div>

                  <div className="mb-3 flex justify-center">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`rounded-full bg-violet-600 px-6 py-2 text-sm font-semibold text-white transition-colors ${
                        loading
                          ? "cursor-not-allowed bg-gray-400"
                          : "hover:bg-violet-700"
                      }`}
                    >
                      {loading ? (
                        <svg
                          className="mx-auto size-5 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          ></path>
                        </svg>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="rounded-b-xl bg-yellow-100 px-4 py-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="m-0 text-xs leading-none text-gray-700">
                      By submitting, I agree to be contacted by Trichos.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowTerms(!showTerms)}
                      className="flex shrink-0 items-center gap-1 text-xs text-gray-700"
                    >
                      Terms
                      <FaChevronDown
                        className={`size-3 transition-transform ${
                          showTerms ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                  {showTerms && (
                    <p className="mt-2 text-xs leading-tight text-gray-700">
                      Protected by Privacy Policy and Terms. This site is
                      protected by reCAPTCHA and the Google{" "}
                      <Link
                        className="text-blue-600 underline"
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link
                        className="text-blue-600 underline"
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </Link>{" "}
                      apply.
                    </p>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
